import { useCallback, useEffect, useRef, useState } from "react"

import { DepartmentJobListing } from "./department-job-listing"
import { CADEPARTMENTNAMES, OFFICE_LOCATIONS } from "./helpers"
import { OfficeButton } from "./office-button"
import { Department, Job } from "./types"

export const CareersList = () => {
  const dropdownContainerRef = useRef<HTMLDivElement>(null)
  const [allJobs, setAllJobs] = useState<Array<Job>>([])
  const [jobs, setJobs] = useState<Array<Job>>([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [currentSelectedOffice, setCurrentSelectedOffice] =
    useState("All offices")

  const jobAPIUrl =
    "https://boards-api.greenhouse.io/v1/boards/cultureamp/jobs?content=true"

  const filterJobsByLocation = useCallback(
    (office: string, jobs: Job[]) => {
      if (office === "All offices") {
        return allJobs
      }

      const jobsFilteredByLocation = jobs.filter(function (job) {
        return job.location.name.includes(office)
      })

      return jobsFilteredByLocation
    },
    [allJobs]
  )

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(jobAPIUrl)
        const apiResult = await response.json()

        setAllJobs(apiResult.jobs)
        const urlParams = new URLSearchParams(window.location.search)
        const officeLocationValue = urlParams.get("officeLocation")
        if (officeLocationValue) {
          const filteredJobs = filterJobsByLocation(
            officeLocationValue,
            apiResult.jobs
          )
          setCurrentSelectedOffice(officeLocationValue)
          setJobs(filteredJobs)
        } else {
          setJobs(apiResult.jobs)
        }
      } catch {
        // no-op
      }
    }
    fetchData()
  }, [filterJobsByLocation])

  function handleDropdownClick() {
    if (dropdownOpen) {
      closeDropdown()
    } else {
      setDropdownOpen(true)
    }
  }

  function handleOfficeButtonClick(location: string) {
    return () => {
      setCurrentSelectedOffice(location)
      const filteredJobs = filterJobsByLocation(location, allJobs)
      setJobs(filteredJobs)
      closeDropdown()
    }
  }

  function closeDropdown() {
    setDropdownOpen(false)
  }

  function getDepartments(jobs: Array<Job>) {
    let departments: Array<Department> = []

    jobs.forEach(function (job) {
      const jobDepartment = job.departments[0]?.name
      const jobDepartmentId = job.departments[0]?.id

      if (!jobDepartment || !jobDepartmentId) {
        return
      }

      const jobsOfSameDepartment = jobs.filter(function (job) {
        return job.departments[0]?.name == jobDepartment
      })
      const numOfJobsPerDepartment: number = jobsOfSameDepartment.length
      const depExistsInArray: number = departments.filter(function (depName) {
        return depName.name == jobDepartment
      }).length
      if (!depExistsInArray) {
        departments.push({
          name: jobDepartment,
          numOfJobs: numOfJobsPerDepartment,
          jobs: jobsOfSameDepartment,
          id: jobDepartmentId,
        })
      }
    })

    // Find Risk and Security departments
    const securityDepartment = departments.find(department => {
      return department.name === "Security"
    })
    const riskDepartment = departments.find(department => {
      return department.name === "Risk"
    })
    // If both exist, merge them into one "Security" department
    if (securityDepartment && riskDepartment) {
      const mergedDepartment = {
        name: securityDepartment.name,
        numOfJobs: securityDepartment.numOfJobs + riskDepartment.numOfJobs,
        jobs: securityDepartment.jobs.concat(riskDepartment.jobs),
        id: securityDepartment.id,
      }
      // Remove the existing Security and Risk departments
      departments = departments.filter(department => {
        return department.name !== "Security" && department.name !== "Risk"
      })
      // Add the merged department
      departments.push(mergedDepartment)
    } else if (!securityDepartment && riskDepartment) {
      // If only Risk department exists, rename it to Security
      riskDepartment.name = "Security"
    }

    // modify Greenhouse response department names
    departments.forEach(department => {
      CADEPARTMENTNAMES.map(depName => {
        if (department.name === depName.greenHouseDepOrSubDepName) {
          department.name = depName.caDepOrSubDepName
        }
      })
    })

    // Group department and subdepartment by name (department and subdepartment are the same in the greenHouse response)
    const groupedDepartments = departments.reduce(
      (items: Department[], department) => {
        const { name, numOfJobs } = department
        const itemIndex = items.findIndex(
          department => department.name === name
        )
        if (itemIndex === -1) {
          items.push(department)
        } else {
          const existingItem = items[itemIndex]
          if (existingItem) {
            existingItem.jobs = existingItem.jobs.concat(department.jobs)
            existingItem.numOfJobs += numOfJobs
          }
        }
        // sort jobs alphabetically
        items.map(item => {
          item.jobs.sort((jobA, jobB) => {
            return jobA.title > jobB.title
              ? 1
              : jobA.title < jobB.title
                ? -1
                : 0
          })
        })
        return items
      },
      []
    )

    // sort departments alphabetically
    groupedDepartments.sort((depA, depB) => {
      return depA.name > depB.name ? 1 : depA.name < depB.name ? -1 : 0
    })
    return groupedDepartments
  }

  type MouseAndTouchEvents = MouseEvent | TouchEvent

  useEffect(() => {
    const handleClickOutside = (event: MouseAndTouchEvents) => {
      const dropdownContainerEL =
        dropdownContainerRef?.current as HTMLInputElement | null
      if (
        !dropdownContainerEL ||
        dropdownContainerEL.contains(event.target as Node)
      ) {
        return
      }
      closeDropdown()
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        closeDropdown()
      }
    }

    // Click outside to close dropdown
    document.addEventListener("mousedown", handleClickOutside)
    // Escape to close dropdown
    document.addEventListener("keydown", handleEscape)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [])

  return (
    <section>
      <div
        className="flex flex-col mobile:flex-row items-start mobile:items-center mb-36"
        data-test="job-listing__dropdown"
      >
        <h3 className="text-16 number-of-jobs">
          {jobs?.length} open position
          <span className={`${jobs?.length == 1 ? "hidden" : "inline"}`}>
            s
          </span>{" "}
          in
        </h3>
        <div
          className="relative mobile:ml-12 mt-12 mobile:mt-0"
          style={{ minWidth: "140px" }}
          ref={dropdownContainerRef}
        >
          <button
            data-test="job-listing__dropdown-button"
            className="dropdown-button office-locations__dropdown-button flex justify-between items-center p-12 whitespace-nowrap"
            onClick={handleDropdownClick}
            id="job-listing__dropdown-button"
            aria-haspopup="true"
            aria-expanded={dropdownOpen}
          >
            <span
              className="text-14 mr-12"
              aria-label={`open roles in ${currentSelectedOffice}`}
            >
              {dropdownOpen ? "Select" : currentSelectedOffice}
            </span>
            <svg
              aria-hidden="true"
              aria-labelledby="chevron-down__dropdown-button"
              className={`transform h-10 w-10 ${dropdownOpen && "rotate-180"}`}
              focusable="false"
              height="9"
              role="img"
              viewBox="0 0 10 8"
              width="10"
              xmlns="http://www.w3.org/2000/svg"
            >
              <title id="chevron-down__dropdown-button">Down chevron</title>
              <path
                clipRule="evenodd"
                d="m1.17917.844971 3.82083 3.901729 3.82083-3.901729 1.17917 1.204129-5 5.10587-5-5.10587z"
                fillRule="evenodd"
              ></path>
            </svg>
          </button>
          <div
            className={`dropdown-menu-container ${
              !dropdownOpen ? "hidden" : "!block"
            }`}
            aria-labelledby="job-listing__dropdown-button"
            role="menu"
          >
            {OFFICE_LOCATIONS.map(location => {
              return (
                <OfficeButton
                  key={location}
                  officeLocation={location}
                  isSelected={currentSelectedOffice === location}
                  onClick={handleOfficeButtonClick(location)}
                />
              )
            })}
          </div>
        </div>
      </div>
      <div
        className={!jobs ? "hidden" : "block"}
        data-test="job-listing__accordion"
      >
        {jobs?.length ? (
          jobs &&
          getDepartments(jobs).map(department => {
            return (
              <DepartmentJobListing
                department={department}
                key={department.id}
              />
            )
          })
        ) : (
          <p className="font-semibold text-16 pl-24">
            No open positions in {currentSelectedOffice} at this moment.
          </p>
        )}
      </div>
    </section>
  )
}
